/**
 * AI quality check evidence: the grade the run stored (pass rate against the pass bar), its model,
 * cost, time and tokens, linking to the run page. Nothing is recomputed here.
 *
 * @module features/proofs/components/EvalEvidenceBody
 */
import React from 'react';
import { Text } from '@chatsdet/ui';
import { formatPassPercent } from '@chatsdet/types/agent-eval-result';
import { Link } from '@tanstack/react-router';
import type { ProofEvidence } from '../types';

type Eval = Extract<ProofEvidence, { kind: 'eval' }>;


/** The row's one-line form: the stored grade. */
export const evalRowSummary = (result: NonNullable<Eval['result']>): string =>
  `${formatPassPercent(result.passRate)} vs target ${formatPassPercent(result.passBar)}`;

export const EvalEvidenceBody: React.FC<{ item: Eval }> = ({ item }) => {
  const { result } = item;
  if (!result) return <Text color="muted">This run has expired, so the result is no longer available.</Text>;
  const details = [
    `${formatPassPercent(result.passRate)} of cases passed (${result.casesPassed} of ${result.cases}), target ${formatPassPercent(result.passBar)}`,
    `${result.model.alias} (${result.model.id})`,
    result.costCentsPerTask !== null ? `$${(result.costCentsPerTask / 100).toFixed(3)} per task` : null,
    `${(result.durationMsPerTask / 1000).toFixed(1)}s per task`,
    `${result.tokens.toLocaleString('en-US')} tokens`,
  ].filter((line): line is string => line !== null);
  return (
    <div className="space-y-1">
      {details.map((line, index) => (
        <Text key={index} className="tabular-nums">{line}</Text>
      ))}
      <Link to="/agent-evals/runs/$runId" params={{ runId: item.runId }} className="text-sm underline hover:text-foreground">
        {`Open ${result.eval} run`}
      </Link>
    </div>
  );
};
